import React from "react";
import { graphql } from "gatsby";
import { MdFileDownload } from "react-icons/md";

import formatCurrency from "../helpers/format-currency";
import { getBidType, getBidText } from "../helpers/sale";

import SectionTitle from "../components/SectionTitle";
import MediaCarousel from "../components/MediaCarousel";
import Button, { ButtonTargets } from "../components/Button";

const SaleDetails: React.FC<{
  sale: Queries.SaleDetailsSaleFragment;
}> = ({ sale }) => {
  const parcel = sale.parcels[0];
  const media = [...(parcel?.videos || []), ...(parcel?.images || [])];

  const bidType = getBidType(sale);

  return (
    <div className="mx-auto mb-16 max-w-[1200px] px-4">
      <SectionTitle>Sale Details</SectionTitle>
      <div className="lg:grid lg:grid-cols-[1fr_384px] lg:gap-x-12">
        {media.length > 0 && (
          <MediaCarousel media={media} className="mb-2 lg:min-w-0" />
        )}
        <div>
          <div className="mb-8">
            <h1 className="mb-2 mt-0 text-[28px] font-bold text-clhbid-orange lg:text-4xl">
              {formatCurrency(parcel[bidType] || 0, "standard")}{" "}
              <span className="font-light italic text-white">
                {getBidText(sale, true, true)}
              </span>
            </h1>
            <p className="mb-4 text-xl font-bold">
              {parcel.size && <span className="font-normal italic">for </span>}
              {parcel.size}
              <span className="font-normal italic"> near </span>
              {sale.location}
            </p>
            <Button
              outline
              external
              size="small"
              family="primary"
              disabled={!parcel.location}
              href={
                parcel.location
                  ? `https://maps.google.com/?t=k&q=${parcel.location.lat},${parcel.location.lon}`
                  : undefined
              }
              target={ButtonTargets.blank}
            >
              Get Directions
            </Button>
          </div>
          <div className="mb-8">
            <h3 className="mb-3 text-2xl font-semibold">Documents To Review</h3>
            <div className="flex flex-col gap-4">
              {parcel.offerToPurchase || parcel.additionalFiles?.length > 0 ? (
                <>
                  {parcel.offerToPurchase && (
                    <Button
                      external
                      family="primary"
                      href={parcel.offerToPurchase.url}
                      className="flex items-center justify-between rounded-md px-4 py-2 text-left"
                      target={ButtonTargets.blank}
                    >
                      {parcel.offerToPurchase.title}
                      <MdFileDownload className="ml-2 h-6 w-6 shrink-0 fill-clhbid-charcoal" />
                    </Button>
                  )}
                  {parcel.additionalFiles?.map((document) => (
                    <a
                      key={document.url}
                      href={document.url}
                      className="flex rounded-md border border-clhbid-orange bg-clhbid-gray px-4 py-[9px] transition-colors hover:bg-clhbid-gray-dark"
                      target={ButtonTargets.blank}
                      rel="noreferrer"
                    >
                      <span className="text-md inline-block grow font-medium">
                        {document.title}
                      </span>
                      <MdFileDownload className="ml-2 h-6 w-6 shrink-0 fill-clhbid-orange" />
                    </a>
                  ))}
                </>
              ) : (
                <Button
                  external
                  disabled
                  family="primary"
                  className="flex items-center justify-between rounded-md px-4 py-2 text-left"
                >
                  Documents Coming Soon
                  <MdFileDownload className="ml-2 h-6 w-6 shrink-0 fill-clhbid-charcoal" />
                </Button>
              )}
            </div>
          </div>
          {parcel.details?.length > 0 && (
            <div>
              <h3 className="mb-3 text-2xl font-semibold">
                Legal Land Descriptions
              </h3>
              <div className="overflow-hidden rounded-md bg-clhbid-gray px-4 py-[2px]">
                <table className="w-full">
                  <tbody>
                    {parcel.details.map((detail) => (
                      <tr
                        key={detail.location}
                        className="border-0 border-b border-gray-400 font-medium last:border-b-0"
                      >
                        <td className="py-2 text-left">{detail.location}</td>
                        <td className="py-2 text-right">{detail.size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SaleDetailsFragment = graphql`
  fragment SaleDetailsSale on ContentfulSale {
    location
    saleStatus
    parcels {
      name
      slug
      acceptedBid
      startingBid
      size
      location {
        lat
        lon
      }
      offerToPurchase {
        url
        title
      }
      additionalFiles {
        url
        title
      }
      details {
        size
        location
      }
      images {
        ...MediaCarouselImage
      }
      videos {
        ...MediaCarouselVideo
      }
      ...SaleDateParcel
    }
  }
`;

export default SaleDetails;
